import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
    catchError,
    exhaustMap,
    map,
    mergeMap,
    withLatestFrom
} from 'rxjs/operators';
import {
    Draft,
    DraftService,
    EditListingRequest,
    ListingRequestType,
    PhotoService,
    RelistListingRequest
} from 'libs/datalayer/src';
import * as DraftActions from './draft.actions';
import * as DraftFeature from './draft.reducer';
import * as DraftSelectors from './draft.selectors';
import { ActivatedRoute } from '@angular/router';

@Injectable()
export class DraftEffects {
    loadDraft$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.getDraft),
            withLatestFrom(this.store.select(DraftSelectors.getState)),
            mergeMap(([action, state]) => {
                if (state && state.categoryId > 0 && state.draftLoaded) {
                    return [DraftActions.noOpAction()];
                }

                const draft$ = action.listingId
                    ? this.draftService.getListingDraft<Draft>(
                          action.listingId,
                          action.categoryId,
                          action.subCategory
                      )
                    : this.draftService.getIncompleteDraft<Draft>(
                          action.categoryId,
                          action.subCategory
                      );

                return draft$.pipe(
                    map((data: Draft) => {
                        return DraftActions.getDraftSuccess({
                            draft: data
                        });
                    }),
                    catchError((error: unknown) =>
                        of(DraftActions.getDraftFailure({ error }))
                    )
                );
            })
        )
    );

    saveDraft$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.saveDraft),
            exhaustMap((action) =>
                this.draftService.saveDraft(action.draft).pipe(
                    map((response) =>
                        DraftActions.saveDraftSuccess({
                            result: response,
                            lastSavedStepId: JSON.parse(action.draft.draftData)
                                .lastSavedStepId
                        })
                    ),
                    catchError((error: unknown) =>
                        of(DraftActions.saveDraftFailure({ error }))
                    )
                )
            )
        )
    );

    makeListingAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.makeListingAction),
            exhaustMap((action) => {
                let apiRequest$;

                switch (action.listingRequest.type) {
                    case ListingRequestType.Create: {
                        apiRequest$ = this.draftService.createListing(action.listingRequest.categoryId, action.listingRequest.subCategory);
                        break;
                    }

                    case ListingRequestType.Edit: {
                        const editRequest =
                            action.listingRequest as EditListingRequest;
                        apiRequest$ = this.draftService.editListing(
                            editRequest.listingId,
                            editRequest.categoryId,
                            editRequest.draft,
                            editRequest.listingEndDate,
                            editRequest.subCategory);
                        break;
                    }

                    case ListingRequestType.Relist: {
                        const relistRequest =
                            action.listingRequest as RelistListingRequest;
                        apiRequest$ = this.draftService.relistListing(
                            relistRequest.listingId,
                            relistRequest.categoryId,
                            relistRequest.draft,
                            relistRequest.subCategory
                        );
                        break;
                    }
                }

                return apiRequest$.pipe(
                    map((response) =>
                        DraftActions.makeListingActionSuccess({
                            listingRequest: response,
                            listingRequestType: action.listingRequest.type
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            DraftActions.makeListingActionFailure({
                                error,
                                listingRequestType: action.listingRequest.type
                            })
                        )
                    )
                );
            })
        )
    );


    validateDraftListing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.validateListing),
            exhaustMap(() => {
                return this.draftService.validateDraftListing().pipe(
                    map((response) =>
                        DraftActions.validateListingSuccess({
                            validListing: response
                        })
                    ),
                    catchError((error: unknown) =>
                        of(DraftActions.validateListingFailure({ error }))
                    )
                );
            })
        )
    );

    addPhotoToDraftData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.addPhotoToDraftData),
            map((action) =>
                DraftActions.uploadPhoto({
                    photoId: action.photo.photoId,
                    selectedPhotoData: action.selectedPhotoData,
                    parentProperty: action.parentProperty
                })
            )
        )
    );

    addAgentPhotoToDraftData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.addAgentPhotoToDraftData),
            map((action) =>
                DraftActions.uploadAgentPhoto({
                    photoId: action.photo.photoId,
                    selectedPhotoData: action.selectedPhotoData,
                    agenttContactDetailsType: action.agenttContactDetailsType,
                    parentProperty: action.parentProperty
                })
            )
        )
    );

    uploadPhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.uploadPhoto),
            mergeMap((action) =>
                this.photoService.uploadPhoto(action.selectedPhotoData).pipe(
                    map((response) =>
                        DraftActions.uploadPhotoSuccess({
                            photoId: action.photoId,
                            photoUrls: response,
                            parentProperty: action.parentProperty
                        })
                    ),
                    catchError((error: unknown) => {
                        return of(
                            DraftActions.uploadPhotoFailure({
                                photoId: action.photoId,
                                error: error,
                                parentProperty: action.parentProperty
                            })
                        );
                    })
                )
            )
        )
    );

    uploadAgentPhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.uploadAgentPhoto),
            mergeMap((action) =>
                this.photoService.uploadAgentPhoto(action.selectedPhotoData).pipe(
                    map((response) =>
                        DraftActions.uploadAgentPhotoSuccess({
                            photoId: response.photoId,
                            photoUrl: response.thumbnail,
                            agenttContactDetailsType: action.agenttContactDetailsType,
                            parentProperty: action.parentProperty
                        })
                    ),
                    catchError((error: unknown) => {
                        return of(
                            DraftActions.uploadAgentPhotoFailure({
                                photoId: action.photoId,
                                error: error,
                                agenttContactDetailsType: action.agenttContactDetailsType,
                                parentProperty: action.parentProperty
                            })
                        );
                    })
                )
            )
        )
    );

    uploadPhotoSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.uploadPhotoSuccess),
            mergeMap((action) =>
                this.loadPhoto(action.photoUrls.plusSize).pipe(
                    map(() => DraftActions.noOpAction()),
                    catchError(() => {
                        return of(
                            DraftActions.updateInvalidPhoto({
                                photoId: action.photoUrls.photoId,
                                errorMessage:
                                    'Error occurred while loading the photo',
                                parentProperty: action.parentProperty
                            })
                        );
                    })
                )
            )
        )
    );

    updateTransformPhotoDataToDraftData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.updateTransformPhotoDataToDraftData),
            map((action) =>
                DraftActions.transformPhoto({
                    photoId: action.editedPhotoData.photoId,
                    editedPhotoData: action.editedPhotoData,
                    parentProperty: action.parentProperty
                })
            )
        )
    );

    transformPhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.transformPhoto),
            mergeMap((action) =>
                this.photoService
                    .transformPhoto({
                        ...action.editedPhotoData,
                        crop:
                            action.editedPhotoData.crop ||
                            action.editedPhotoData.maxBoundingBox // ahh.. TM API expects something to be provided always,
                        // but user has no intention of cropping the image
                        // so provide the full bounding box (o_O)
                    })
                    .pipe(
                        map((response) =>
                            DraftActions.transformPhotoSuccess({
                                photoId: action.photoId,
                                transformedPhoto: response,
                                crop: action.editedPhotoData.crop,
                                parentProperty: action.parentProperty
                            })
                        ),
                        catchError((error: unknown) => {
                            return of(
                                DraftActions.transformPhotoFailure({
                                    photoId: action.photoId,
                                    error: error,
                                    parentProperty: action.parentProperty
                                })
                            );
                        })
                    )
            )
        )
    );

    transformPhotoSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.transformPhotoSuccess),
            mergeMap((action) =>
                this.loadPhoto(action.transformedPhoto.plusSize).pipe(
                    map(() => DraftActions.noOpAction()),
                    catchError(() => {
                        return of(
                            DraftActions.updateInvalidPhoto({
                                photoId: action.photoId,
                                errorMessage:
                                    'Error occurred while loading the photo',
                                parentProperty: action.parentProperty
                            })
                        );
                    })
                )
            )
        )
    );

    updateCurrentStep$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DraftActions.updateCurrentStep),
            exhaustMap((action) =>
                this.draftService.updateCurrentStep(action.currentStep).pipe(
                    map((response) =>
                        DraftActions.updateCurrentStepSuccess({
                            result: response,
                            currentStepId: action.currentStep.currentStepId
                        })
                    ),
                    catchError((error: unknown) =>
                        of(DraftActions.updateCurrentStepFailure({ error }))
                    )
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store<DraftFeature.DraftPartialState>,
        private draftService: DraftService,
        private photoService: PhotoService,
        private route: ActivatedRoute
    ) {}

    private loadPhoto(url: string) {
        return new Observable<any>((o) => {
            const img = new Image();
            img.src = url;
            img.onload = () => {
                o.complete();
            };
            img.onerror = (err) => {
                o.error(err);
            };
        });
    }
}
